/**
 * Smart job description parser
 * Extracts structured information from raw HTML/text job descriptions
 */

/**
 * Extract technologies/skills from description
 */
export const extractTechnologies = (text) => {
    if (!text) return [];

    const techKeywords = [
        // Languages
        'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'PHP',
        // Frontend
        'React', 'Vue', 'Angular', 'Next.js', 'Svelte', 'HTML', 'CSS', 'Tailwind', 'Bootstrap',
        // Backend
        'Node.js', 'Express', 'Django', 'Flask', 'Spring', 'Rails', 'Laravel',
        // Databases
        'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Elasticsearch', 'DynamoDB',
        // Cloud
        'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Terraform',
        // Tools
        'Git', 'Jenkins', 'CI/CD', 'GraphQL', 'REST', 'API',
        // Misc
        'Machine Learning', 'AI', 'Data Science', 'DevOps', 'Agile', 'Scrum',
    ];

    const found = new Set();
    const lowerText = text.toLowerCase();

    techKeywords.forEach(tech => {
        // Escape special characters for regex (like C++, C#)
        const escapedTech = tech.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Use word boundaries for matching, handling special cases like C++ or C#
        const regex = new RegExp(`(?:^|[^a-zA-Z0-9])${escapedTech}(?:$|[^a-zA-Z0-9])`, 'i');

        if (regex.test(text)) {
            found.add(tech);
        }
    });

    return Array.from(found).slice(0, 8); // Limit to 8
};

/**
 * Extract salary information
 */
export const extractSalary = (text) => {
    if (!text) return null;

    // Patterns for salary ranges
    const patterns = [
        /\$[\d,]+\s*-\s*\$[\d,]+/gi, // $100,000 - $150,000
        /\$[\d,]+k\s*-\s*\$[\d,]+k/gi, // $100k - $150k
        /[\d,]+\s*-\s*[\d,]+\s*(?:USD|usd|dollar)/gi, // 100,000 - 150,000 USD
    ];

    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
            return match[0];
        }
    }

    return null;
};

/**
 * Convert HTML to clean plain text
 */
const htmlToText = (html) => {
    if (!html) return '';

    // Replace <br/> and <br> tags with actual newlines
    let text = html.replace(/<br\s*\/?>/gi, '\n');

    // Replace </p>, </div>, </li> with newlines
    text = text.replace(/<\/(p|div|li|h[1-6])>/gi, '\n');

    // Extract text from links but preserve the text content
    text = text.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1');

    // Remove all remaining HTML tags
    text = text.replace(/<[^>]*>/g, ' ');

    // Decode common HTML entities
    text = text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

    // Clean up multiple spaces and newlines
    text = text.replace(/  +/g, ' ');
    text = text.replace(/\n\s*\n\s*\n/g, '\n\n');

    return text.trim();
};

/**
 * Parse job description into sections
 */
export const parseJobDescription = (description) => {
    if (!description) {
        return {
            requirements: [],
            responsibilities: [],
            benefits: [],
            technologies: [],
            salary: null,
            rawText: '',
        };
    }

    // Convert HTML to clean plain text
    const plainText = htmlToText(description);

    const result = {
        requirements: [],
        responsibilities: [],
        benefits: [],
        technologies: extractTechnologies(plainText),
        salary: extractSalary(plainText),
        rawText: plainText,
    };

    // Split into lines
    const lines = plainText.split('\n').map(l => l.trim()).filter(Boolean);

    let currentSection = null;

    lines.forEach(line => {
        const lowerLine = line.toLowerCase();

        // Detect section headers
        if (lowerLine.includes('requirement') || lowerLine.includes('qualification')) {
            currentSection = 'requirements';
            return;
        }
        if (lowerLine.includes('responsibilit') || lowerLine.includes('you will') || lowerLine.includes('duties')) {
            currentSection = 'responsibilities';
            return;
        }
        if (lowerLine.includes('benefit') || lowerLine.includes('we offer') || lowerLine.includes('perks')) {
            currentSection = 'benefits';
            return;
        }

        // Add to current section if it looks like a bullet point
        if (currentSection && (line.startsWith('-') || line.startsWith('•') || line.startsWith('*') || /^\d+\./.test(line))) {
            const cleanedLine = line.replace(/^[-•*]\s*/, '').replace(/^\d+\.\s*/, '').trim();
            if (cleanedLine.length > 10) { // Minimum length filter
                result[currentSection].push(cleanedLine);
            }
        }
    });

    return result;
};

/**
 * Shorten description for preview
 */
export const createDescriptionPreview = (description, maxLength = 150) => {
    if (!description) return '';

    const plainText = description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    if (plainText.length <= maxLength) return plainText;

    return plainText.substring(0, maxLength).trim() + '...';
};

/**
 * Detect if description contains legal/diversity text
 */
export const hasLegalText = (text) => {
    if (!text) return false;

    const legalPatterns = [
        'equal opportunity employer',
        'eoe',
        'affirmative action',
        'ada',
        'disability',
        'veteran',
        'does not discriminate',
    ];

    const lowerText = text.toLowerCase();
    return legalPatterns.some(pattern => lowerText.includes(pattern));
};
